import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Badge, Button, Container, Form, Modal, Spinner} from 'react-bootstrap';
import {FaComments, FaCopy, FaSearch, FaShareAlt, FaSignOutAlt, FaTimes, FaUserPlus, FaUsers, FaWhatsapp} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import {ROLES} from '../constants';
import CommonMetaTag from './common-meta-tag';
import WebPushSettings from './web-push-settings';
import {
  cancelOpenMatch,
  confirmOpenMatchResult,
  confirmRepAction,
  createPartnerOpenMatchInvitations,
  getMyOpenMatchByPublicId,
  getOpenMatchInviteSuggestions,
  getPublicOpenMatch,
  joinOpenMatch,
  leaveOpenMatch,
  partnerGetPlayers,
  playerGetPlayers,
  prepareRepAction,
  saveOpenMatchFeedback,
  submitOpenMatchResult
} from '../api';
import {hasInvitedLevelOverride} from '../util/open-match-invitations';

const PENDING_JOIN_KEY = 'openMatchPendingJoinPublicId';

const sportValue = value => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') return value.name || value.label || value.value || value.sport || value.type || '';
  return String(value);
};
const normalizeSport = value => sportValue(value)
  .toLowerCase()
  .trim()
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');
const sportDisplayName = sport => {
  const normalized = normalizeSport(sport);
  if (normalized === 'tennis') return 'Tennis';
  if (normalized === 'padel') return 'Padel';
  if (normalized === 'pickleball') return 'Pickleball';
  return sportValue(sport) || normalized;
};

const MATCH_TYPE_LABELS = {
  friendly: 'OPEN_MATCH_TYPE_FRIENDLY',
  competitive: 'OPEN_MATCH_TYPE_COMPETITIVE'
};

const GENDER_LABELS = {
  any: 'OPEN_MATCH_GENDER_ANY',
  male: 'OPEN_MATCH_GENDER_MALE',
  female: 'OPEN_MATCH_GENDER_FEMALE',
  mixed: 'OPEN_MATCH_GENDER_MIXED'
};

const STATUS_LABELS = {
  open: 'OPEN_MATCH_STATUS_OPEN',
  full: 'OPEN_MATCH_STATUS_FULL',
  confirmed: 'OPEN_MATCH_STATUS_CONFIRMED',
  cancelled: 'OPEN_MATCH_STATUS_CANCELLED',
  expired: 'OPEN_MATCH_STATUS_EXPIRED',
  completed: 'OPEN_MATCH_STATUS_COMPLETED'
};

const formatTime = value => value ? new Date(value).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '';
const formatDate = (value, locale = 'it-IT') => value ? new Date(value).toLocaleDateString(locale, {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'}) : '';
const formatMoney = cents => `${(Number(cents || 0) / 100).toFixed(2)} €`;
const fullName = user => (user && (user.fullName || `${user.name || ''} ${user.surname || ''}`.trim())) || 'Player Replayer';
const levelLabel = (value, t) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? t('LEVEL_VALUE', {value: numericValue.toFixed(1)}) : null;
};

const friendlyError = (error, reason, t) => {
  const code = reason || error;
  const key = {
    not_enough_available_credit: 'OPEN_MATCH_DETAIL_ERROR_NOT_ENOUGH_CREDIT',
    level_too_low: 'OPEN_MATCH_DETAIL_ERROR_LEVEL_TOO_LOW',
    level_too_high: 'OPEN_MATCH_DETAIL_ERROR_LEVEL_TOO_HIGH',
    ranking_required: 'OPEN_MATCH_DETAIL_ERROR_RANKING_REQUIRED',
    open_match_full: 'OPEN_MATCH_DETAIL_ERROR_FULL',
    slot_no_longer_available: 'OPEN_MATCH_DETAIL_ERROR_SLOT_UNAVAILABLE',
    open_match_invitation_already_pending: 'OPEN_MATCH_DETAIL_ERROR_INVITE_PENDING',
    open_match_user_already_joined: 'OPEN_MATCH_DETAIL_ERROR_ALREADY_JOINED',
    open_match_invite_not_allowed: 'OPEN_MATCH_DETAIL_ERROR_INVITE_NOT_ALLOWED',
    cannot_invite_self: 'OPEN_MATCH_DETAIL_ERROR_CANNOT_INVITE_SELF',
    invited_user_not_found: 'OPEN_MATCH_DETAIL_ERROR_PLAYER_NOT_FOUND',
    rep_action_expired: 'REP_ACTION_ERROR_EXPIRED',
    gender_required: 'OPEN_MATCH_DETAIL_ERROR_GENDER_REQUIRED',
    gender_not_allowed: 'OPEN_MATCH_DETAIL_ERROR_GENDER_NOT_ALLOWED',
    mixed_gender_quota_full: 'OPEN_MATCH_DETAIL_ERROR_MIXED_QUOTA_FULL',
    mixed_open_match_requires_doubles: 'OPEN_MATCH_DETAIL_ERROR_MIXED_REQUIRES_DOUBLES',
    open_match_not_finished: 'OPEN_MATCH_DETAIL_ERROR_NOT_FINISHED',
    open_match_result_invalid_score: 'OPEN_MATCH_DETAIL_ERROR_INVALID_SCORE',
    open_match_result_invalid_teams: 'OPEN_MATCH_DETAIL_ERROR_INVALID_TEAMS',
    open_match_result_already_accepted: 'OPEN_MATCH_DETAIL_ERROR_RESULT_ACCEPTED'
  }[code];
  return t(key || 'OPEN_MATCH_OPERATION_FAILED');
};

const OpenMatchDetailPage = ({match, token, profile, history, location}) => {
  const {t} = useTranslation();
  const locale = t('LOCALE_CODE', 'it-IT');
  const publicId = match.params.publicId;
  const [publicMatch, setPublicMatch] = useState(null);
  const [privateMatch, setPrivateMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [inviteSearchDone, setInviteSearchDone] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState(null);
  const [inviteAction, setInviteAction] = useState({status: 'idle', item: null, error: null});
  const [eligibleInviteCount, setEligibleInviteCount] = useState(0);
  const [selectedInviteUids, setSelectedInviteUids] = useState([]);
  const [resultSets, setResultSets] = useState([{team1: '', team2: ''}, {team1: '', team2: ''}, {team1: '', team2: ''}]);
  const [resultTeams, setResultTeams] = useState({});
  const [feedbackValues, setFeedbackValues] = useState({});

  const loadMatch = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    const publicRes = await getPublicOpenMatch(publicId);
    if (publicRes.status === 200) {
      setPublicMatch(publicRes.json.openMatch);
    } else {
      setMessage(t('OPEN_MATCH_DETAIL_NOT_FOUND'));
    }
    if (token) {
      const privateRes = await getMyOpenMatchByPublicId(publicId, token);
      if (privateRes.status === 200) {
        setPrivateMatch(privateRes.json.openMatch);
      } else {
        setPrivateMatch(null);
      }
    } else {
      setPrivateMatch(null);
    }
    setLoading(false);
  }, [publicId, token, t]);

  const currentMatch = useMemo(() => privateMatch || publicMatch, [privateMatch, publicMatch]);
  const participants = useMemo(() => (currentMatch && currentMatch.participants) || [], [currentMatch]);
  const participantUids = useMemo(() => participants.map(player => player.uid).filter(Boolean), [participants]);
  const pendingInvites = useMemo(() => (privateMatch && privateMatch.invitations) || [], [privateMatch]);
  const emptyPlaces = useMemo(() => currentMatch ? Math.max(0, currentMatch.playersRequired - participants.length) : 0, [currentMatch, participants]);
  const currentResult = useMemo(() => privateMatch && privateMatch.result ? privateMatch.result : null, [privateMatch]);
  const matchFinished = useMemo(() => currentMatch && currentMatch.endDatetime ? new Date(currentMatch.endDatetime).getTime() <= Date.now() : false, [currentMatch]);
  const canPostMatch = useMemo(() => privateMatch && privateMatch.currentUserJoined && matchFinished && ['completed', 'confirmed'].includes(privateMatch.status), [matchFinished, privateMatch]);
  const shareText = useMemo(() => currentMatch ? t('OPEN_MATCH_SHARE_TEXT', {
    sport: sportDisplayName(currentMatch.sport),
    club: currentMatch.partnerName,
    date: formatDate(currentMatch.startDatetime, locale),
    time: formatTime(currentMatch.startDatetime),
    count: emptyPlaces
  }) : '', [currentMatch, emptyPlaces, locale, t]);

  useEffect(() => {
    loadMatch();
  }, [loadMatch]);

  useEffect(() => {
    if (!token || !privateMatch) return;
    if (window.sessionStorage.getItem(PENDING_JOIN_KEY) === publicId) {
      window.sessionStorage.removeItem(PENDING_JOIN_KEY);
      if (privateMatch.canJoin) {
        setJoinModalOpen(true);
      }
    }
  }, [privateMatch, publicId, token]);

  useEffect(() => {
    if (!canPostMatch || currentResult || !participants.length) return;
    setResultTeams(currentTeams => {
      const expectedUids = participants.map(player => String(player.uid)).filter(Boolean);
      const hasAllParticipants = expectedUids.every(uid => currentTeams[uid]);
      if (hasAllParticipants) return currentTeams;
      const nextTeams = {};
      participants.forEach((player, index) => {
        if (!player.uid) return;
        nextTeams[player.uid] = index < Math.ceil(participants.length / 2) ? 'team1' : 'team2';
      });
      return nextTeams;
    });
  }, [canPostMatch, currentResult, participants]);

  const resultTeamUids = useMemo(() => {
    const team1Uids = [];
    const team2Uids = [];
    participants.forEach(player => {
      if (!player.uid) return;
      if (resultTeams[player.uid] === 'team1') team1Uids.push(player.uid);
      if (resultTeams[player.uid] === 'team2') team2Uids.push(player.uid);
    });
    return {team1Uids, team2Uids};
  }, [participants, resultTeams]);

  const participantNameByUid = useMemo(() => {
    const names = {};
    participants.forEach(player => {
      if (player.uid) names[player.uid] = player.displayName || player.fullName || t('PLAYER_NUMBER', {id: player.uid});
    });
    return names;
  }, [participants, t]);

  const participantRows = useMemo(() => {
    const rows = participants.map((participant, index) => ({key: `p-${index}`, participant}));
    for (let index = 0; index < emptyPlaces; index += 1) {
      rows.push({key: `e-${index}`, participant: null});
    }
    return rows;
  }, [emptyPlaces, participants]);

  const publicUrl = `${window.location.origin}/matches/${publicId}`;
  const isAuthenticated = Boolean(token && profile);
  const isPartner = Boolean(profile && profile.user && profile.user.role === ROLES.partner);
  const isOpen = currentMatch && ['open', 'full'].includes(currentMatch.status);
  const isCreator = privateMatch && profile && privateMatch.creatorUid === profile.uid;
  const canInvite = privateMatch && (privateMatch.currentUserJoined || isCreator) && privateMatch.status === 'open';
  const canChat = privateMatch && privateMatch.currentUserJoined && ['open', 'full', 'confirmed'].includes(privateMatch.status);
  const canJoin = privateMatch ? privateMatch.canJoin : isOpen;
  const invitedLevelOverride = hasInvitedLevelOverride(privateMatch);

  const requireLoginForJoin = () => {
    window.sessionStorage.setItem(PENDING_JOIN_KEY, publicId);
    history.push(`/login?next=${encodeURIComponent(location.pathname)}`);
  };

  const doJoin = async () => {
    if (!privateMatch) return;
    setActionLoading(true);
    const res = await joinOpenMatch(privateMatch.id, token);
    if (res.status === 200) {
      setJoinModalOpen(false);
      setPrivateMatch(res.json.openMatch);
      await loadMatch();
      setMessage(res.json.openMatch.status === 'confirmed' ? t('OPEN_MATCH_JOIN_CONFIRMED') : t('OPEN_MATCH_JOINED'));
    } else {
      setMessage(friendlyError(res.json && res.json.error, res.json && res.json.reason, t));
    }
    setActionLoading(false);
  };

  const doLeave = async () => {
    const leaveMessage = privateMatch && privateMatch.requiresOnlinePayment === false
      ? t('OPEN_MATCH_CONFIRM_LEAVE_NO_PAYMENT')
      : t('OPEN_MATCH_CONFIRM_LEAVE');
    if (!privateMatch || !window.confirm(leaveMessage)) return;
    setActionLoading(true);
    const res = await leaveOpenMatch(privateMatch.id, token);
    if (res.status === 200) {
      setPrivateMatch(res.json.openMatch);
      await loadMatch();
      setMessage(t('OPEN_MATCH_LEFT'));
    } else {
      setMessage(friendlyError(res.json && res.json.error, null, t));
    }
    setActionLoading(false);
  };

  const doCancel = async () => {
    const cancelMessage = privateMatch && privateMatch.requiresOnlinePayment === false
      ? t('OPEN_MATCH_CONFIRM_CANCEL_NO_PAYMENT')
      : t('OPEN_MATCH_CONFIRM_CANCEL');
    if (!privateMatch || !window.confirm(cancelMessage)) return;
    setActionLoading(true);
    const res = await cancelOpenMatch(privateMatch.id, token);
    if (res.status === 200) {
      setPrivateMatch(res.json.openMatch);
      await loadMatch();
      setMessage(t('OPEN_MATCH_CANCELLED_MESSAGE'));
    } else {
      setMessage(friendlyError(res.json && res.json.error, null, t));
    }
    setActionLoading(false);
  };

  const openInviteModal = async () => {
    if (!privateMatch) return;
    setInviteModalOpen(true);
    setInviteFeedback(null);
    setInviteAction({status: 'idle', item: null, error: null});
    setSelectedInviteUids([]);
    setInviteLoading(true);
    const res = await getOpenMatchInviteSuggestions(privateMatch.id, token);
    setSuggestions(res.status === 200 ? res.json.players || [] : []);
    setEligibleInviteCount(res.status === 200 ? Number(res.json.eligibleCount || 0) : 0);
    setInviteLoading(false);
  };

  const sendPartnerInvites = async inviteAllEligible => {
    if (!privateMatch || !isPartner) return;
    setInviteLoading(true);
    setInviteFeedback(null);
    const selectedCompatibleCount = suggestions.filter(player => (
      player.levelCompatible && selectedInviteUids.includes(player.uid)
    )).length;
    const res = await createPartnerOpenMatchInvitations(privateMatch.id, {
      inviteAllEligible,
      invitedUids: inviteAllEligible ? [] : selectedInviteUids
    }, token);
    if (res.status === 201) {
      const invitedUids = new Set((res.json.invitations || []).map(invitation => invitation.invitedUid));
      setPrivateMatch(res.json.openMatch);
      setSuggestions(currentSuggestions => currentSuggestions.filter(player => !invitedUids.has(player.uid)));
      setSelectedInviteUids([]);
      const compatibleInvitedCount = inviteAllEligible
        ? Number(res.json.invitedCount || 0)
        : selectedCompatibleCount;
      setEligibleInviteCount(currentCount => Math.max(0, currentCount - compatibleInvitedCount));
      setInviteFeedback(t('OPEN_MATCH_BULK_INVITE_SENT', {count: Number(res.json.invitedCount || 0)}));
    } else {
      setInviteFeedback(friendlyError(res.json && res.json.error, null, t));
    }
    setInviteLoading(false);
  };

  const sendInvite = async playerOrUid => {
    if (!privateMatch) return;
    const uid = playerOrUid && typeof playerOrUid === 'object' ? (playerOrUid.uid || playerOrUid.id) : playerOrUid;
    setInviteLoading(true);
    setInviteFeedback(null);
    setInviteAction({status: 'preparing', item: null, error: null});
    const res = await prepareRepAction({
      actionType: 'open-match.invite',
      parameters: {openMatchId: privateMatch.id, invitedUid: uid},
      locale,
      sourcePath: location.pathname
    }, token);
    if (res.status === 201) {
      setInviteAction({status: 'prepared', item: res.json.action, error: null});
    } else {
      const error = res.json && res.json.error;
      setInviteAction({status: 'error', item: null, error});
      setInviteFeedback(friendlyError(error, null, t));
    }
    setInviteLoading(false);
  };

  const confirmInvite = async () => {
    const action = inviteAction.item;
    if (!action || !token) return;
    setInviteLoading(true);
    setInviteFeedback(null);
    setInviteAction(current => ({...current, status: 'confirming', error: null}));
    const res = await confirmRepAction(action.id, `rep-action-${action.id}`, token);
    if (res.status === 200 && res.json && res.json.action && res.json.action.status === 'succeeded') {
      const preview = action.preview || {};
      const invitedUid = preview.invitedUid;
      setInviteFeedback(t('OPEN_MATCH_INVITE_SENT_TO', {name: preview.invitedName || t('PLAYER')}));
      setSuggestions(currentSuggestions => currentSuggestions.filter(player => player.uid !== invitedUid));
      if (preview.levelCompatible) {
        setEligibleInviteCount(currentCount => Math.max(0, currentCount - 1));
      }
      setSelectedInviteUids(currentUids => currentUids.filter(selectedUid => selectedUid !== invitedUid));
      setSearchResults(currentResults => currentResults.filter(player => (player.uid || player.id) !== invitedUid));
      setInviteSearch('');
      setInviteSearchDone(false);
      setInviteAction({status: 'idle', item: null, error: null});
      await loadMatch();
    } else {
      const error = res.json && res.json.error;
      setInviteAction(current => ({...current, status: 'error', error}));
      setInviteFeedback(friendlyError(error, null, t));
    }
    setInviteLoading(false);
  };

  const searchPlayers = async () => {
    if (!inviteSearch.trim()) return;
    setInviteLoading(true);
    setInviteSearchDone(false);
    const query = inviteSearch.trim();
    const slotId = privateMatch && privateMatch.slotId ? privateMatch.slotId : '';
    const partnerId = privateMatch && privateMatch.partnerId ? privateMatch.partnerId : '';
    const responses = isPartner
      ? await Promise.all([
        partnerGetPlayers(query, '', '', false, token),
        partnerGetPlayers('', query, '', false, token),
        partnerGetPlayers('', '', query, false, token)
      ])
      : await Promise.all([
        playerGetPlayers(query, '', '', false, slotId, partnerId, token),
        playerGetPlayers('', query, '', false, slotId, partnerId, token),
        playerGetPlayers('', '', query, false, slotId, partnerId, token)
      ]);
    const resultsByUid = {};
    responses.forEach(res => {
      if (res.status !== 200 || !Array.isArray(res.json)) return;
      res.json.forEach(player => {
        const key = player.uid || player.id;
        if (key) resultsByUid[key] = player;
      });
    });
    setSearchResults(Object.values(resultsByUid));
    setInviteSearchDone(true);
    setInviteLoading(false);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({title: t('OPEN_MATCH_SHARE_TITLE'), text: shareText, url: publicUrl});
        return;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
    }
    setShareModalOpen(true);
  };

  const copyLink = async () => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(publicUrl);
    } else {
      const input = document.createElement('textarea');
      input.value = publicUrl;
      input.setAttribute('readonly', '');
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setMessage(t('LINK_COPIED'));
    setShareModalOpen(false);
  };

  const formatTeamNames = uids => (uids || []).map(uid => participantNameByUid[uid] || t('PLAYER_NUMBER', {id: uid})).join(', ');

  const submitResult = async () => {
    if (!privateMatch) return;
    const sets = resultSets
      .filter(row => row.team1 !== '' && row.team2 !== '')
      .map(row => ({team1: Number(row.team1), team2: Number(row.team2)}));
    if (!sets.length) {
      setMessage(t('OPEN_MATCH_RESULT_INSERT_SET'));
      return;
    }
    if (
      resultTeamUids.team1Uids.length < 1 ||
      resultTeamUids.team2Uids.length < 1 ||
      resultTeamUids.team1Uids.length + resultTeamUids.team2Uids.length !== participantUids.length
    ) {
      setMessage(t('OPEN_MATCH_RESULT_SPLIT_TEAMS'));
      return;
    }
    setActionLoading(true);
    const res = await submitOpenMatchResult(privateMatch.id, {
      team1Uids: resultTeamUids.team1Uids,
      team2Uids: resultTeamUids.team2Uids,
      sets
    }, token);
    if (res.status === 201) {
      setPrivateMatch(res.json.openMatch);
      setMessage(t('OPEN_MATCH_RESULT_SUBMITTED'));
      await loadMatch();
    } else {
      setMessage(friendlyError(res.json && res.json.error, null, t));
    }
    setActionLoading(false);
  };

  const confirmResult = async accepted => {
    if (!currentResult) return;
    setActionLoading(true);
    const res = await confirmOpenMatchResult(currentResult.id, accepted, token);
    if (res.status === 200) {
      setPrivateMatch(res.json.openMatch);
      setMessage(accepted ? t('OPEN_MATCH_RESULT_CONFIRMED_MESSAGE') : t('OPEN_MATCH_RESULT_REJECTED_MESSAGE'));
      await loadMatch();
    } else {
      setMessage(friendlyError(res.json && res.json.error, null, t));
    }
    setActionLoading(false);
  };

  const saveFeedback = async () => {
    if (!privateMatch) return;
    const feedback = Object.keys(feedbackValues).map(uid => ({reviewedUid: Number(uid), value: Number(feedbackValues[uid])}));
    if (!feedback.length) {
      setMessage(t('OPEN_MATCH_FEEDBACK_SELECT_ONE'));
      return;
    }
    setActionLoading(true);
    const res = await saveOpenMatchFeedback(privateMatch.id, feedback, token);
    if (res.status === 200) {
      setPrivateMatch(res.json.openMatch);
      setMessage(t('OPEN_MATCH_FEEDBACK_SAVED'));
    } else {
      setMessage(friendlyError(res.json && res.json.error, null, t));
    }
    setActionLoading(false);
  };

  if (loading) {
    return <Container className="open-match-detail-page"><div className="open-match-v1-loading"><Spinner animation="border"/>{t('OPEN_MATCH_DETAIL_LOADING')}</div></Container>;
  }

  if (!currentMatch) {
    return <Container className="open-match-detail-page"><Alert variant="warning">{t('OPEN_MATCH_DETAIL_NOT_FOUND')}</Alert></Container>;
  }

  return (
    <Container className="open-match-detail-page">
      <CommonMetaTag/>
      <header className={`open-match-detail-hero open-match-detail-hero--${currentMatch.status}`}>
        <span><FaUsers aria-hidden="true"/> {t(STATUS_LABELS[currentMatch.status] || currentMatch.status)}</span>
        <h1>{currentMatch.title || `${sportDisplayName(currentMatch.sport)} · ${currentMatch.partnerName}`}</h1>
        <div className="open-match-detail-hero-time">
          <div><small>{t('DAY')}</small><strong>{formatDate(currentMatch.startDatetime, locale)}</strong></div>
          <div><small>{t('TIME')}</small><strong>{formatTime(currentMatch.startDatetime)}-{formatTime(currentMatch.endDatetime)}</strong></div>
          <div><small>{t('FIELD')}</small><strong>{currentMatch.partnerName} · {currentMatch.fieldName}</strong></div>
        </div>
      </header>

      {message ? <Alert variant="info" className="open-match-v1-alert">{message}</Alert> : null}

      <section className="open-match-detail-grid">
        <article className="open-match-v1-card open-match-detail-main">
          <div className="open-match-detail-status">
            <Badge variant={currentMatch.status === 'confirmed' ? 'success' : currentMatch.status === 'open' ? 'primary' : 'secondary'}>{t(STATUS_LABELS[currentMatch.status] || currentMatch.status)}</Badge>
            <strong>{currentMatch.status === 'open' ? t('OPEN_MATCH_LOOKING_FOR_PLAYERS', {count: emptyPlaces}) : t(STATUS_LABELS[currentMatch.status] || currentMatch.status)}</strong>
            {currentMatch.status === 'open' ? <small>{t('OPEN_MATCH_FIELD_CONFIRMED_WHEN_COMPLETE')}</small> : null}
            {currentMatch.status === 'confirmed' ? <small>{t('OPEN_MATCH_FIELD_GUARANTEED')}</small> : null}
            {currentMatch.status === 'expired' ? <small>{t('OPEN_MATCH_EXPIRED_REASON')}</small> : null}
            {currentMatch.status === 'cancelled' ? <small>{t('OPEN_MATCH_CANCELLED_REASON')}</small> : null}
          </div>
          <div className="open-match-v1-summary-grid">
            <div><span>{t('DAY')}</span><strong>{formatDate(currentMatch.startDatetime, locale)}</strong></div>
            <div><span>{t('TIME')}</span><strong>{formatTime(currentMatch.startDatetime)}-{formatTime(currentMatch.endDatetime)}</strong></div>
            <div><span>{t('SPORT')}</span><strong>{sportDisplayName(currentMatch.sport)}</strong></div>
            <div><span>{t('OPEN_MATCH_FORMAT')}</span><strong>{Number(currentMatch.playersRequired) === 2 ? t('OPEN_MATCH_FORMAT_SINGLE') : t('OPEN_MATCH_FORMAT_DOUBLE')}</strong></div>
            <div><span>{t('TYPE')}</span><strong>{t(MATCH_TYPE_LABELS[currentMatch.matchType] || currentMatch.matchType)}</strong></div>
            <div><span>{t('OPEN_MATCH_GENDER')}</span><strong>{t(GENDER_LABELS[currentMatch.gender] || 'OPEN_MATCH_GENDER_ANY')}</strong></div>
            <div><span>{t('LEVEL')}</span><strong>{currentMatch.minLevel !== null && currentMatch.maxLevel !== null ? `${Number(currentMatch.minLevel).toFixed(1)} - ${Number(currentMatch.maxLevel).toFixed(1)}` : t('OPEN_MATCH_LEVEL_ANY')}</strong></div>
            <div><span>{t('PLAYERS')}</span><strong>{currentMatch.playersJoined}/{currentMatch.playersRequired}</strong></div>
            <div><span>{t('OPEN_MATCH_PARTICIPANT_FEE')}</span><strong>{formatMoney(currentMatch.participantPriceCents)}</strong></div>
          </div>
          {currentMatch.notes ? <p className="open-match-detail-notes">{currentMatch.notes}</p> : null}
          <div className="open-match-detail-actions">
            {!isAuthenticated && isOpen ? <Button className="open-match-v1-primary" onClick={requireLoginForJoin}>{t('JOIN')}</Button> : null}
            {isAuthenticated && !privateMatch ? <Button className="open-match-v1-primary" disabled>{t('OPEN_MATCH_LOADING_ACTIONS')}</Button> : null}
            {privateMatch && !isPartner && !privateMatch.currentUserJoined && isOpen ? <Button className="open-match-v1-primary" disabled={!canJoin || actionLoading} onClick={() => setJoinModalOpen(true)}>{t(invitedLevelOverride ? 'OPEN_MATCH_JOIN_ANYWAY' : 'JOIN')}</Button> : null}
            {privateMatch && privateMatch.currentUserJoined && isOpen && !isCreator ? <Button className="open-match-detail-danger-action" onClick={doLeave} disabled={actionLoading}><FaSignOutAlt aria-hidden="true"/>{t('OPEN_MATCH_LEAVE')}</Button> : null}
            {isCreator && isOpen ? <Button className="open-match-detail-danger-action" onClick={doCancel} disabled={actionLoading}><FaTimes aria-hidden="true"/>{t('OPEN_MATCH_CANCEL')}</Button> : null}
            {canInvite ? <Button className="open-match-detail-secondary-action" onClick={openInviteModal}><FaUserPlus aria-hidden="true"/>{t('OPEN_MATCH_INVITE_PLAYER')}</Button> : null}
            {canChat ? <Button className="open-match-detail-chat-action" onClick={() => history.push(`/messages?openMatch=${privateMatch.id}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</Button> : null}
            <Button className="open-match-detail-share-button" onClick={shareNative}><FaShareAlt aria-hidden="true"/>{t('SHARE')}</Button>
          </div>
          {privateMatch && privateMatch.joinBlockReason && !privateMatch.currentUserJoined ? <Alert variant="warning" className="mt-3">{friendlyError(privateMatch.joinBlockReason, null, t)}</Alert> : null}
          {canPostMatch ? (
            <div className="open-match-result-panel">
              <h2>{t('OPEN_MATCH_RESULT_AND_FEEDBACK')}</h2>
              {currentResult ? (
                <Fragment>
                  <div className="open-match-result-score">
                    <span>{t('RESULT')} {currentResult.status === 'accepted' ? t('OPEN_MATCH_RESULT_CONFIRMED') : currentResult.status === 'rejected' ? t('OPEN_MATCH_RESULT_REJECTED') : t('OPEN_MATCH_RESULT_PENDING')}</span>
                    <strong>{(currentResult.sets || []).map(set => `${set.team1}-${set.team2}`).join(' / ')}</strong>
                  </div>
                  <div className="open-match-result-teams">
                    <div><span>{t('TEAM_1')}</span><strong>{formatTeamNames(currentResult.team1Uids)}</strong></div>
                    <div><span>{t('TEAM_2')}</span><strong>{formatTeamNames(currentResult.team2Uids)}</strong></div>
                  </div>
                  {currentResult.status === 'pending' ? (
                    <div className="open-match-detail-actions">
                      <Button className="open-match-v1-primary" disabled={actionLoading} onClick={() => confirmResult(true)}>{t('OPEN_MATCH_CONFIRM_RESULT')}</Button>
                      <Button className="open-match-detail-danger-action" disabled={actionLoading} onClick={() => confirmResult(false)}>{t('REJECT')}</Button>
                    </div>
                  ) : null}
                  {currentResult.ratingApplied ? <Alert variant="success" className="open-match-v1-alert">{t('OPEN_MATCH_RANKING_UPDATED_BY_RESULT')}</Alert> : null}
                </Fragment>
              ) : (
                <Fragment>
                  <p className="open-match-ranking-intro">{t('OPEN_MATCH_RESULT_HELP')}</p>
                  <div className="open-match-result-team-picker">
                    {participants.map(player => (
                      <div key={player.uid} className="open-match-result-team-player">
                        <strong>{player.displayName || player.fullName}</strong>
                        {levelLabel(player.level, t) ? <small>{levelLabel(player.level, t)}</small> : null}
                        <Form.Control
                          as="select"
                          value={resultTeams[player.uid] || ''}
                          onChange={event => setResultTeams({...resultTeams, [player.uid]: event.target.value})}
                        >
                          <option value="">{t('CHOOSE_TEAM')}</option>
                          <option value="team1">{t('TEAM_1')}</option>
                          <option value="team2">{t('TEAM_2')}</option>
                        </Form.Control>
                      </div>
                    ))}
                  </div>
                  <div className="open-match-result-teams">
                    <div><span>{t('TEAM_1')}</span><strong>{formatTeamNames(resultTeamUids.team1Uids) || t('TO_CHOOSE')}</strong></div>
                    <div><span>{t('TEAM_2')}</span><strong>{formatTeamNames(resultTeamUids.team2Uids) || t('TO_CHOOSE')}</strong></div>
                  </div>
                  <div className="open-match-result-sets">
                    <div className="open-match-result-sets-header">
                      <span>{t('SET')}</span>
                      <span>{formatTeamNames(resultTeamUids.team1Uids) || t('TEAM_1')}</span>
                      <span>{formatTeamNames(resultTeamUids.team2Uids) || t('TEAM_2')}</span>
                    </div>
                    {resultSets.map((set, index) => (
                      <div key={index}>
                        <span>{t('SET_NUMBER', {number: index + 1})}</span>
                        <Form.Control type="number" min="0" inputMode="numeric" value={set.team1} placeholder={t('TEAM_1')} aria-label={`${t('SET_NUMBER', {number: index + 1})} ${t('TEAM_1')}`} onChange={event => {
                          const next = resultSets.slice();
                          next[index] = {...next[index], team1: event.target.value};
                          setResultSets(next);
                        }}/>
                        <Form.Control type="number" min="0" inputMode="numeric" value={set.team2} placeholder={t('TEAM_2')} aria-label={`${t('SET_NUMBER', {number: index + 1})} ${t('TEAM_2')}`} onChange={event => {
                          const next = resultSets.slice();
                          next[index] = {...next[index], team2: event.target.value};
                          setResultSets(next);
                        }}/>
                      </div>
                    ))}
                  </div>
                  <Button className="open-match-v1-primary" disabled={actionLoading || participantUids.length < Number(currentMatch.playersRequired)} onClick={submitResult}>{t('SAVE_RESULT')}</Button>
                </Fragment>
              )}
              <h3>{t('OPEN_MATCH_RATE_PLAYERS_TITLE')}</h3>
              <p className="open-match-ranking-intro">{t('OPEN_MATCH_RATE_PLAYERS_HELP')}</p>
              <div className="open-match-feedback-list">
                {participants.filter(player => profile && player.uid !== profile.uid).map(player => (
                  <div key={player.uid} className="open-match-feedback-row">
                    <strong>{player.displayName || player.fullName}</strong>
                    <Form.Control as="select" value={feedbackValues[player.uid] || ''} onChange={event => setFeedbackValues({...feedbackValues, [player.uid]: event.target.value})}>
                      <option value="">{t('CHOOSE')}</option>
                      <option value="-1">{t('OPEN_MATCH_FEEDBACK_LOWER')}</option>
                      <option value="0">{t('OPEN_MATCH_FEEDBACK_CORRECT')}</option>
                      <option value="1">{t('OPEN_MATCH_FEEDBACK_HIGHER')}</option>
                    </Form.Control>
                  </div>
                ))}
              </div>
              <Button className="open-match-detail-secondary-action" disabled={actionLoading} onClick={saveFeedback}>{t('SAVE_FEEDBACK')}</Button>
            </div>
          ) : null}
        </article>

        <aside className="open-match-v1-card open-match-detail-side">
          <h2>{t('PARTICIPANTS')}</h2>
          <div className="open-match-detail-participant-list">
            {participantRows.map(row => row.participant ? (
              <div key={row.key} className="open-match-detail-player">
                <span>
                  {row.participant.displayName || row.participant.fullName}
                  {levelLabel(row.participant.level, t) ? <b>{levelLabel(row.participant.level, t)}</b> : null}
                </span>
                <small>{t('PARTICIPANT')}</small>
              </div>
            ) : (
              <div key={row.key} className="open-match-detail-player open-match-detail-player--empty">
                <span>{t('OPEN_MATCH_FREE_SPOT')}</span>
                <small>{t('PENDING')}</small>
              </div>
            ))}
          </div>
          {pendingInvites.length > 0 ? (
            <Fragment>
              <h3>{t('INVITED')}</h3>
              <div className="open-match-detail-invites">
                {pendingInvites.map(invitation => <span key={invitation.id}>{invitation.invitedFullName || t('PLAYER_NUMBER', {id: invitation.invitedUid})} - {t('PENDING')}</span>)}
              </div>
            </Fragment>
          ) : null}
          {privateMatch && privateMatch.currentUserJoined ? <WebPushSettings token={token} compact/> : null}
        </aside>
      </section>

      <Modal show={joinModalOpen} onHide={() => setJoinModalOpen(false)} centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('JOIN')}</span>
            <Modal.Title>{t('OPEN_MATCH_CONFIRM_YOUR_FEE')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          <div className="open-match-v1-summary-grid">
            <div><span>{t('MATCH')}</span><strong>{sportDisplayName(currentMatch.sport)}</strong></div>
            <div><span>{t('PLAYER')}</span><strong>{profile ? `${profile.name || ''} ${profile.surname || ''}`.trim() || t('YOU') : t('YOU')}{privateMatch && privateMatch.currentUserLevel ? ` · ${levelLabel(privateMatch.currentUserLevel, t)}` : ''}</strong></div>
            <div><span>{t('OPEN_MATCH_PARTICIPANT_FEE')}</span><strong>{formatMoney(currentMatch.participantPriceCents)}</strong></div>
          </div>
          <Alert variant="info" className="open-match-v1-field-note">
            {currentMatch.requiresOnlinePayment === false
              ? t('OPEN_MATCH_JOIN_PAY_AT_CLUB')
              : t('OPEN_MATCH_JOIN_HOLD')}
          </Alert>
          {invitedLevelOverride ? (
            <Alert variant="warning" className="open-match-v1-field-note">
              {t('OPEN_MATCH_INVITED_LEVEL_OVERRIDE_WARNING')}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setJoinModalOpen(false)}>{t('CLOSE')}</Button>
          <Button className="profile-auth-action profile-auth-action--save" disabled={actionLoading} onClick={doJoin}>{t(invitedLevelOverride ? 'OPEN_MATCH_JOIN_ANYWAY' : 'JOIN')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={shareModalOpen} onHide={() => setShareModalOpen(false)} centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('SHARE')}</span>
            <Modal.Title>{t('OPEN_MATCH_SHARE_MODAL_TITLE')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          <p className="open-match-ranking-intro">{shareText}</p>
          <div className="open-match-detail-share-actions">
            <a className="btn open-match-v1-primary" href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${publicUrl}`)}`} target="_blank" rel="noopener noreferrer"><FaWhatsapp aria-hidden="true"/>WhatsApp</a>
            <Button className="open-match-detail-share-button" onClick={copyLink}><FaCopy aria-hidden="true"/>{t('COPY_LINK')}</Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={inviteModalOpen} onHide={() => setInviteModalOpen(false)} size="lg" centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('INVITE')}</span>
            <Modal.Title>{t('OPEN_MATCH_INVITE_PLAYER')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          {inviteAction.item ? (
            <div className="open-match-invite-confirmation" role="status" aria-live="polite">
              <div>
                <strong>{t('OPEN_MATCH_INVITE_CONFIRM_TITLE')}</strong>
                <span>{t('OPEN_MATCH_INVITE_CONFIRM_HELP', {name: inviteAction.item.preview.invitedName})}</span>
                <small>{inviteAction.item.preview.invitedLevel !== null && inviteAction.item.preview.invitedLevel !== undefined
                  ? t('OPEN_MATCH_INVITE_CONFIRM_LEVEL', {level: Number(inviteAction.item.preview.invitedLevel).toFixed(1)})
                  : t('OPEN_MATCH_COMPATIBILITY_CHECKED_ON_ACCEPT')}</small>
                {!inviteAction.item.preview.levelCompatible ? <small className="is-warning">{t('OPEN_MATCH_INVITE_OUTSIDE_LEVEL_WARNING')}</small> : null}
              </div>
              <div>
                <Button className="open-match-detail-share-button" disabled={inviteLoading} onClick={() => setInviteAction({status: 'idle', item: null, error: null})}>{t('CANCEL')}</Button>
                <Button className="open-match-v1-primary" disabled={inviteLoading} onClick={confirmInvite}>
                  <FaUserPlus aria-hidden="true"/>{t('OPEN_MATCH_INVITE_CONFIRM_SEND')}
                </Button>
              </div>
            </div>
          ) : null}
          <h3>{t('SUGGESTED')}</h3>
          {inviteFeedback ? <Alert variant="info" className="open-match-v1-alert">{inviteFeedback}</Alert> : null}
          {inviteLoading ? <div className="open-match-v1-loading"><Spinner animation="border" size="sm"/>{t('LOADING')}</div> : null}
          {isPartner && suggestions.length > 0 ? (
            <div className="open-match-partner-invite-tools">
              <p>{t('OPEN_MATCH_ELIGIBLE_AFFILIATES', {count: eligibleInviteCount})}</p>
              <div>
                <Button
                  className="open-match-detail-secondary-action"
                  disabled={inviteLoading || selectedInviteUids.length === 0}
                  onClick={() => sendPartnerInvites(false)}
                >
                  <FaUserPlus aria-hidden="true"/>{t('OPEN_MATCH_INVITE_SELECTED', {count: selectedInviteUids.length})}
                </Button>
                <Button className="open-match-v1-primary" disabled={inviteLoading || eligibleInviteCount === 0} onClick={() => sendPartnerInvites(true)}>
                  <FaUsers aria-hidden="true"/>{t('OPEN_MATCH_INVITE_ALL_ELIGIBLE')}
                </Button>
              </div>
            </div>
          ) : null}
          <div className="open-match-invite-list">
            {suggestions.map(player => (
              <div key={player.uid} className="open-match-invite-row">
                {isPartner ? (
                  <Form.Check
                    type="checkbox"
                    aria-label={t('OPEN_MATCH_SELECT_PLAYER', {name: player.fullName || fullName(player)})}
                    checked={selectedInviteUids.includes(player.uid)}
                    onChange={event => setSelectedInviteUids(current => event.target.checked
                      ? [...current, player.uid]
                      : current.filter(uid => uid !== player.uid))}
                  />
                ) : null}
                <span>
                  <strong>{player.fullName || fullName(player)}</strong>
                  <small>{player.level !== null && player.level !== undefined ? t('LEVEL_VALUE', {value: Number(player.level).toFixed(1)}) : t('OPEN_MATCH_COMPATIBILITY_CHECKED_ON_ACCEPT')}</small>
                  {!player.levelCompatible ? <small className="is-warning">{t('OPEN_MATCH_INVITE_OUTSIDE_LEVEL_SHORT')}</small> : null}
                </span>
                <Button size="sm" className="open-match-v1-primary" disabled={inviteAction.status !== 'idle'} onClick={() => sendInvite(player)}>{t('INVITE')}</Button>
              </div>
            ))}
            {!inviteLoading && suggestions.length === 0 ? <p className="open-match-ranking-intro">{t('OPEN_MATCH_NO_SUGGESTIONS')}</p> : null}
          </div>
          <h3>{t('SEARCH_PLAYER')}</h3>
          <Form className="open-match-invite-search" onSubmit={event => {
            event.preventDefault();
            searchPlayers();
          }}>
            <Form.Control value={inviteSearch} placeholder={t('OPEN_MATCH_SEARCH_PLAYER_PLACEHOLDER')} onChange={event => {
              setInviteSearch(event.target.value);
              setInviteSearchDone(false);
            }}/>
            <Button type="submit" className="open-match-detail-secondary-action" disabled={inviteLoading || inviteSearch.trim().length < 2}><FaSearch aria-hidden="true"/>{t('SEARCH')}</Button>
          </Form>
          <div className="open-match-invite-list">
            {searchResults.map(player => (
              <div key={player.uid || player.id} className="open-match-invite-row">
                <span><strong>{fullName(player)}</strong><small>{player.level ? t('LEVEL_VALUE', {value: Number(player.level).toFixed(1)}) : t('OPEN_MATCH_COMPATIBILITY_CHECKED_ON_ACCEPT')}</small></span>
                <Button size="sm" className="open-match-v1-primary" disabled={inviteAction.status !== 'idle'} onClick={() => sendInvite(player)}>{t('INVITE')}</Button>
              </div>
            ))}
            {inviteSearchDone && searchResults.length === 0 ? <p className="open-match-ranking-intro">{t('OPEN_MATCH_NO_PLAYERS_FOUND')}</p> : null}
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

OpenMatchDetailPage.propTypes = {
  match: PropTypes.object.isRequired,
  token: PropTypes.string,
  profile: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

OpenMatchDetailPage.defaultProps = {
  token: null,
  profile: null
};

const mapStateToProps = ({login: {token, profile}}) => ({token, profile});

export default connect(mapStateToProps)(OpenMatchDetailPage);
